
import React, { useState, useCallback, useRef } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  ReactFlowInstance,
  Panel,
  Node,
  Edge,
  Connection,
  EdgeChange,
  NodeChange,
  useNodesState,
  useEdgesState,
  addEdge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { NodeData, EdgeData } from './types';
import WorkflowNode from './WorkflowNode';
import WorkflowEdge from './WorkflowEdge';
import { Plus, Save, ZoomIn, ZoomOut } from 'lucide-react';
import WorkflowConnectionLine from './WorkflowConnectionLine';
import NodeConfigPanel from './NodeConfigPanel';

interface WorkflowEditorProps {
  initialNodes?: Node<NodeData>[];
  initialEdges?: Edge<EdgeData>[];
  readOnly?: boolean;
  onSave?: (nodes: Node<NodeData>[], edges: Edge<EdgeData>[]) => void;
}

// Define node types
const nodeTypes = {
  customNode: WorkflowNode,
};

// Define edge types
const edgeTypes = {
  customEdge: WorkflowEdge,
};

const WorkflowEditor: React.FC<WorkflowEditorProps> = ({
  initialNodes = [],
  initialEdges = [],
  readOnly = false,
  onSave,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState<Node<NodeData> | null>(null);
  const reactFlowInstance = useRef<ReactFlowInstance | null>(null);

  const onInit = useCallback((instance: ReactFlowInstance) => {
    reactFlowInstance.current = instance;
  }, []);

  const onConnect = useCallback(
    (params: Connection) => {
      setEdges((eds) => addEdge({ ...params, type: 'customEdge' }, eds));
    },
    [setEdges]
  );

  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node<NodeData>) => {
      setSelectedNode(node);
    },
    []
  );

  const handleAddNode = useCallback(() => {
    const newNode: Node<NodeData> = {
      id: `node_${Date.now()}`,
      type: 'customNode',
      position: { x: 100, y: 100 },
      data: { 
        id: `node_${Date.now()}`,
        label: 'New Node', 
        type: 'capability',
      },
    };

    setNodes((nds) => [...nds, newNode]);
  }, [setNodes]);

  const handleSave = useCallback(() => {
    if (onSave && reactFlowInstance.current) {
      onSave(nodes, edges);
      toast.success('Workflow saved successfully');
    }
  }, [nodes, edges, onSave]);

  const handleNodeConfigChange = useCallback(
    (nodeId: string, newData: Partial<NodeData>) => {
      setNodes((nds) =>
        nds.map((node) => 
          node.id === nodeId 
            ? { 
                ...node, 
                data: { 
                  ...node.data, 
                  ...newData 
                } 
              } 
            : node
        )
      );

      // Update selected node if it's the one being edited
      if (selectedNode && selectedNode.id === nodeId) {
        setSelectedNode((prevNode) => 
          prevNode 
            ? {
                ...prevNode,
                data: { 
                  ...prevNode.data, 
                  ...newData 
                }
              }
            : null
        );
      }
    },
    [selectedNode, setNodes]
  );

  const handleCloseConfigPanel = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const handleNodeDelete = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));

      if (selectedNode && selectedNode.id === nodeId) {
        setSelectedNode(null);
      }

      toast.info('Node deleted');
    },
    [selectedNode, setNodes, setEdges]
  );

  const handleSelectionChange = useCallback(
    ({ nodes }: { nodes: Node<NodeData>[]; edges: Edge<EdgeData>[] }) => {
      // If a node is selected, show it in the config panel
      if (nodes.length === 1) {
        setSelectedNode(nodes[0]);
      } else if (nodes.length === 0) {
        // If nothing is selected, clear the config panel
        setSelectedNode(null);
      }
    },
    []
  );

  return (
    <div className="h-full w-full flex">
      <div className="flex-1 border rounded-lg overflow-hidden">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onInit={onInit}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          connectionLineComponent={WorkflowConnectionLine}
          onSelectionChange={handleSelectionChange}
          fitView
          snapToGrid
          attributionPosition="bottom-left"
          proOptions={{ hideAttribution: true }}
        >
          <Background />
          <Controls />
          <MiniMap />

          <Panel position="top-right" className="space-x-2">
            {!readOnly && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddNode}
                  className="flex items-center gap-1"
                >
                  <Plus className="h-4 w-4" />
                  Add Node
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSave}
                  className="flex items-center gap-1"
                  disabled={!onSave}
                >
                  <Save className="h-4 w-4" />
                  Save
                </Button>
              </>
            )}
          </Panel>
        </ReactFlow>
      </div>

      {selectedNode && !readOnly && (
        <NodeConfigPanel
          node={selectedNode}
          onClose={handleCloseConfigPanel}
          onChange={handleNodeConfigChange}
          onDelete={handleNodeDelete}
        />
      )}
    </div>
  );
};

export default WorkflowEditor;
